import React from 'react';
import { compose } from 'react-recompose';
import { Route, IndexRedirect, IndexRoute, Redirect } from 'react-router';

import Application from './containers/App';
import CommunityPage from './containers/Community';
import CounselorCommunityServerless from './containers/CounselorCommunityMicro';
import LoginPage from './containers/LoginPage';
import ChangeProfilePage from './containers/ChangeProfilePage';
import NewPassword from './containers/NewPassword';
import ManageUsersPage from './containers/ManageUsersPage';
import SFTPSettingsPage from './containers/SFTPSettingsPage';
import CreateUserPage from './containers/CreateUserPage';
import UpdateUserPage from './containers/UpdateUserPage';
import ForgotPassword from './containers/ForgotPassword';
import UnauthorizedPage from './containers/UnauthorizedPage';
import {
  notAuthorized,
  authorizedOrLogin,
  authorizedWithRole,
  requireCommunityProfile,
} from './containers/Authorization';
import {
  ADMIN_ROLE,
  PUBLISHING_ROLE,
  REP_VISITS_SUBSCRIPTION,
  AM_PLUS_SUBSCRIPTION,
  ADV_AWARENESS_SUBSCRIPTION,
  CONNECTION_SUBSCRIPTION,
  SCHOLARSHIPS_SUBSCRIPTION,
  CAMINO_SUBSCRIPTION,
  COMMUNITY_SERVERLESS,
  DISABLE_LEGACY_COMMUNITY,
} from './containers/Authorization/constants';
import { onFeatureEnabled, onFeatureDisabled } from './utils/features';
import SearchResultsPage from './containers/SearchResultsPage';
import RepVisitsFrame from './components/RepVisitsFrame';
import RepVisitsOverviewFrame from './containers/RepVisitsOverviewFrame';
import RepVisitsRecommendationsPage from './containers/RepVisitsRecommendationsPage';
import RepVisitsSearchPage from './containers/RepVisitsSearchPage';
import RepVisitsTravelPlan from './containers/RepVisitsTravelPlanPage';
import RepVisitsContactsUnlockPage from './containers/RepVisitsContactsPage/repvisits-contacts-unlock';
import RepVisitsRecommendationsUnlockPage from './containers/RepVisitsRecommendationsPage/repvisits-recommendations-unlock';
import RepVisitsTravelPlanUnlockPage from './containers/RepVisitsTravelPlanPage/repvisits-travelplan-unlock';
import RepVisitsCalendarPage from './containers/RepVisitsCalendarPage';
import RepVisitsContactsPage from './containers/RepVisitsContactsPage';
import NewSftpSettingsPage from './containers/NewSFTPConnection';
import RepVisitsNotificationsAndTasksPage from './containers/RepVisitsNotificationsAndTasksPage';
import RepVisitsNotificationsRequests from './containers/RepVisitsNotificationsRequests';
import RepVisitsNotificationsActivity from './containers/RepVisitsNotificationsActivity';
import RepVisitsNotificationsHSAvailable from './containers/RepVisitsNotificationsHSAvailable';
import SFTPSettingsFrame from './components/SFTPSettingsFrame';
import CollegeEventsFrame from './components/CollegeEventsFrame';
import ScholarshipsFrame from './components/ScholarshipsFrame';
import CollegeEventsViewEvents from './containers/CollegeEventsPage';
import CollegeEventsEditEvent from './containers/CollegeEventsEditEvent';
import CollegeEventsFiltersPage from './containers/CollegeEventsFiltersPage';
import CollegeEventsCreateFilterPage from './containers/CollegeEventsCreateFilter';
import CollegeEventsDuplicateFilterPage from './containers/CollegeEventsDuplicateFilterPage';
import AMPlusFrame from './components/AMPlusFrame';
import AMPlusConnectionsPage from './containers/AMPlusConnectionsPage';
import AMPlusFiltersPage from './containers/AMPlusFiltersPage';
import RepVisitsRatingsUnlockPage from './containers/RepVisitsRatingsPage/repvisits-ratings-unlock';
import RepVisitsRatings from './containers/RepVisitsRatingsPage';
import RepVisitsRatingsOverviewPage from './containers/RepVisitsRatingsOverviewPage';
import IntersectSettingsPageContainer from './containers/IntersectSettingsPage';
import InstitutionNotificationSettings from './containers/InstitutionNotificationSettings';
import YourNotifications from './containers/YourNotifications';
import SubscriptionsPage from './containers/SubscriptionsPage';
import ScholarshipsPage from './containers/Scholarships';
import {
  AMNextConfigureThresholdsPage,
  AMNextCompetitorsMessagingPage,
  AMNextMajorsMessagingPage,
} from './containers/AMNextPages';
import AdvAwareConfigureFrame from './containers/AdvancedAwarenessPage';
import ConnectionPage from './containers/ConnectionPage';
import UnderstandingYourSubscription from './containers/UnderstandingYourSubscription';
import AMNextDiversitySettingsPage from './containers/AMNextDiversitySettingsPage';
import CollegeProfileEdit from './containers/CollegeProfileReact/edit';
import CollegeProfileView from './containers/CollegeProfileReact/viewReact';
import { onSubscriptionActive, onAnySubscriptionActive } from './utils/subscriptions';

const authorizedOrLoginAndCommunityProfile = compose(
  authorizedOrLogin,
  requireCommunityProfile,
);

const authorizedWithRoleAndUnauthorizedMessage = (roles) =>
  authorizedWithRole(roles, { failure: UnauthorizedPage });

const loadGraphIQL = process.env.GRAPHIQL && ((location, callback) => {
  import('./containers/GraphQL').then((GraphQL) => {
    callback(null, authorizedOrLogin(GraphQL.default));
  }, callback);
});

export default (
  <Route path="/" component={(props, state, params) => <Application idleTimeout={process.env.IDLE_TIMEOUT} {...props} />}>
    <IndexRoute
      name="home"
      component={
        authorizedOrLogin(SubscriptionsPage)
      }
    />
    <Route
      name="subscription-form"
      path="subscribe/:subscription"
      component={
        authorizedOrLogin(SubscriptionsPage)
      }
    />
    <Route name="login" path="login" component={notAuthorized(LoginPage)} />
    <Route name="new-password" path="new-password" component={notAuthorized(NewPassword)} />
    <Route name="forgot-password" path="forgot-password" component={notAuthorized(ForgotPassword)} />
    <Route
      name="counselor-community-profile-serverless"
      path="/counselorcommunity/*"
      component={compose(
        authorizedOrLogin,
        onFeatureEnabled(COMMUNITY_SERVERLESS),
      )(CounselorCommunityServerless)}
    />
    <Route
      name="counselor-community"
      path="counselor-community/*"
      component={compose(
        authorizedOrLogin,
        onFeatureDisabled(DISABLE_LEGACY_COMMUNITY),
      )(CommunityPage)}
    />
    <Route
      name="search-results"
      path="search-results/:view"
      component={authorizedOrLoginAndCommunityProfile(SearchResultsPage)}
    />
    <Route
      name="collegeProfileEdit"
      path="naviance-college-profile/edit"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE, PUBLISHING_ROLE]),
      )(CollegeProfileEdit)}
    />
    <Route
      name="collegeProfileEdit"
      path="naviance-college-profile/edit/*"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE, PUBLISHING_ROLE]),
      )(CollegeProfileEdit)}
    />
    <Route
      name="collegeProfileView"
      path="naviance-college-profile/viewprofile/:scid"
      component={authorizedOrLoginAndCommunityProfile(CollegeProfileView)}
    />
    <Route
      name="collegeProfileView"
      path="naviance-college-profile/viewprofile/:scid/*"
      component={authorizedOrLoginAndCommunityProfile(CollegeProfileView)}
    />
    <Route
      name="collegeProfileView"
      path="naviance-college-profile/view/:collegeId"
      component={authorizedOrLoginAndCommunityProfile(CollegeProfileView)}
    />
    <Route
      name="collegeProfileView"
      path="naviance-college-profile/view/:collegeId/*"
      component={authorizedOrLoginAndCommunityProfile(CollegeProfileView)}
    />
    <Route
      name="settings"
      path="settings"
      component={authorizedOrLogin(IntersectSettingsPageContainer)}
    >
      <IndexRedirect to="change-profile" />
      <Route
        name="settings-change-profile"
        path="change-profile"
        component={authorizedOrLoginAndCommunityProfile(ChangeProfilePage)}
      />
      <Route
        requiredSubscription={REP_VISITS_SUBSCRIPTION}
        name="settings-your-notifications"
        path="your-notifications"
        component={compose(
          authorizedOrLoginAndCommunityProfile,
          onSubscriptionActive(REP_VISITS_SUBSCRIPTION),
        )(YourNotifications)}
      />
      <Route
        name="settings-institution-notifications"
        requiresAdmin
        requiredSubscription={[AM_PLUS_SUBSCRIPTION, CONNECTION_SUBSCRIPTION]}
        path="institution-notifications"
        component={compose(
          authorizedOrLoginAndCommunityProfile,
          authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
        )(InstitutionNotificationSettings)}
      />
      <Route
        name="settings-institution-users"
        path="manageUsers"
        requiresAdmin
        forceFetch
        component={compose(
          authorizedOrLoginAndCommunityProfile,
          authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
        )(ManageUsersPage)}
      />
      <Route
        name="sftp-settings"
        path="sftp-settings"
        requiresAdmin
        requiredSubscription={[AM_PLUS_SUBSCRIPTION, CONNECTION_SUBSCRIPTION]}
        component={authorizedOrLoginAndCommunityProfile(SFTPSettingsFrame)}
      >
        <IndexRedirect to="sftp-list" />
        <Route
          name="sftp-list"
          path="sftp-list"
          component={compose(
            authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
            onAnySubscriptionActive([AM_PLUS_SUBSCRIPTION, CONNECTION_SUBSCRIPTION], { failure: UnauthorizedPage }),
          )(SFTPSettingsPage)}
        />
        <Route
          name="new-sftp-settings"
          path="new-sftp-settings"
          component={compose(
            authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE]),
            onAnySubscriptionActive([AM_PLUS_SUBSCRIPTION, CONNECTION_SUBSCRIPTION], { failure: UnauthorizedPage }),
          )(NewSftpSettingsPage)}
        />
      </Route>
    </Route>
    <Route
      name="editUser"
      path="manageUsers/edit-user/:userId"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
      )(UpdateUserPage)}
    />
    <Route
      name="addUser"
      path="manageUsers/add-user"
      component={compose(
        authorizedOrLogin,
        authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
      )(CreateUserPage)}
    />
    <Route
      name="repVisits"
      path="rep-visits"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
      )(RepVisitsFrame)}
    >
      <IndexRedirect to="search" />
      <Route
        name="repVisitsOverview"
        path="overview"
        component={RepVisitsOverviewFrame}
      />
      <Route
        name="repVisitsSearch"
        path="search(/:id)(/:availability)"
        component={RepVisitsSearchPage}
      />
      <Route
        name="repVisitsTravelPlan"
        path="travel-plan"
        component={onSubscriptionActive(REP_VISITS_SUBSCRIPTION, { failure: RepVisitsTravelPlanUnlockPage })(RepVisitsTravelPlan)}
      />
      <Route
        name="repVisitsCalendar"
        path="calendar"
        component={RepVisitsCalendarPage}
      />
      <Route
        name="repVisitsContacts"
        path="contacts"
        component={onSubscriptionActive(REP_VISITS_SUBSCRIPTION, { failure: RepVisitsContactsUnlockPage })(RepVisitsContactsPage)}
      />
      <Route
        name="notifications"
        path="notifications"
        component={RepVisitsNotificationsAndTasksPage}
      >
        <IndexRedirect to="requests" />
        <Route
          name="repVisitsNotificationsRequests"
          path="requests"
          component={RepVisitsNotificationsRequests}
        />
        <Route
          name="repVisitsNotificationsActivity"
          path="activity"
          component={RepVisitsNotificationsActivity}
        />
        <Route
          name="repVisitsNotificationsHSAvailable"
          path="hsavailable"
          component={onSubscriptionActive(REP_VISITS_SUBSCRIPTION, { failure: UnauthorizedPage })(RepVisitsNotificationsHSAvailable)}
        />
      </Route>
      <Route
        name="repVisitsRecommendations"
        path="recommendations"
        component={
          onSubscriptionActive(REP_VISITS_SUBSCRIPTION, { failure: RepVisitsRecommendationsUnlockPage })(RepVisitsRecommendationsPage)
        }
      />
      <Route
        name="repVisitsRatings"
        path="visit-feedback"
        component={compose(
          authorizedWithRole(ADMIN_ROLE, { failure: RepVisitsRatingsUnlockPage }),
          onSubscriptionActive(REP_VISITS_SUBSCRIPTION, { failure: RepVisitsRatingsUnlockPage }),
        )(RepVisitsRatings)}
      >
        <IndexRoute
          name="repVisitsRatingsOverview"
          component={RepVisitsRatingsOverviewPage}
        />
      </Route>
    </Route>
    <Route
      name="collegeEvents"
      path="college-events"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE, PUBLISHING_ROLE]),
      )(CollegeEventsFrame)}
    >
      <IndexRedirect to="view-events/published" />
      <Route
        name="collegeEventsViewEvents"
        path="view-events"
        component={CollegeEventsViewEvents}
      />
      <Route
        name="collegeEventsViewEvents"
        path="view-events/published"
        component={CollegeEventsViewEvents}
      />
      <Route
        name="collegeEventsViewEvents"
        path="view-events/unpublished"
        component={CollegeEventsViewEvents}
      />
      <Route
        name="collegeEventsViewEvents"
        path="view-events/expired"
        component={CollegeEventsViewEvents}
      />
      <Route
        name="collegeEventsCreateEvent"
        path="view-events/create/:id"
        component={CollegeEventsEditEvent}
      />
      <Route
        name="collegeEventsEditEvent"
        path="view-events/publish/:id"
        component={CollegeEventsEditEvent}
      />
      <Route
        name="collegeEventsEditEvent"
        path="view-events/edit/:id"
        component={CollegeEventsEditEvent}
      />
      <Route
        name="collegeEventsDuplicateEvent"
        path="view-events/duplicate/:id"
        component={CollegeEventsEditEvent}
      />
      <Route
        name="collegeEventsViewFilters"
        path="view-filters"
        component={CollegeEventsFiltersPage}
      />
      <Route
        name="collegeEventsCreateFilter"
        path="view-filters/create"
        component={CollegeEventsCreateFilterPage}
      />
      <Route
        name="collegeEventsCreateFilter"
        path="view-filters/create/forevent"
        component={CollegeEventsCreateFilterPage}
      />
      <Route
        name="collegeEventsDuplicateFilter"
        path="view-filters/:id/duplicate"
        component={CollegeEventsDuplicateFilterPage}
      />
    </Route>
    <Route
      name="amPlus"
      path="am-plus"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        onSubscriptionActive(AM_PLUS_SUBSCRIPTION, { failure: UnauthorizedPage }),
      )(AMPlusFrame)}
    >
      <IndexRedirect to="view-connections" />
      <Route
        name="amPlusViewConnections"
        path="view-connections"
        component={authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE)(AMPlusConnectionsPage)}
      />
      <Route
        name="amPlusViewFilters"
        path="view-filters"
        component={AMPlusFiltersPage}
      />
    </Route>
    <Route
      name="advancedAwareness"
      path="advanced-awareness"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE, PUBLISHING_ROLE]),
        onSubscriptionActive(ADV_AWARENESS_SUBSCRIPTION, { failure: UnauthorizedPage }),
      )(AdvAwareConfigureFrame)}
    >
      <IndexRedirect to="overview" />
      <Route
        name="advancedAwarenessOverview"
        path="overview"
        component={UnderstandingYourSubscription}
      />
      <Route
        name="advancedAwarenessThreshold"
        path="threshold"
        component={AMNextConfigureThresholdsPage}
      />
      <Route
        name="advancedAwarenessDiversity"
        path="diversity"
        component={AMNextDiversitySettingsPage}
      />
      <Route
        name="advancedAwarenessCompetitors"
        path="competitors"
        component={AMNextCompetitorsMessagingPage}
      />
      <Route
        name="advancedAwarenessMajors"
        path="majors"
        component={AMNextMajorsMessagingPage}
      />
    </Route>
    <Route
      name="connection"
      path="connection"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        authorizedWithRoleAndUnauthorizedMessage(ADMIN_ROLE),
        onSubscriptionActive([CONNECTION_SUBSCRIPTION, CAMINO_SUBSCRIPTION], { failure: UnauthorizedPage }),
      )(ConnectionPage)}
    >
      <IndexRedirect to="connections" />
      <Route
        name="connectionOverview"
        path="overview"
        component={UnderstandingYourSubscription}
      />
      <Route
        name="connectionThreshold"
        path="threshold"
        component={AMNextConfigureThresholdsPage}
      />
      <Route
        name="connectionDiversity"
        path="diversity"
        component={AMNextDiversitySettingsPage}
      />
      <Route
        name="connectionConnections"
        path="connections"
        component={AMPlusConnectionsPage}
      />
    </Route>
    <Route
      name="scholarships"
      path="college-scholarships"
      component={compose(
        authorizedOrLoginAndCommunityProfile,
        onFeatureEnabled(SCHOLARSHIPS_SUBSCRIPTION),
        authorizedWithRoleAndUnauthorizedMessage([ADMIN_ROLE, PUBLISHING_ROLE]),
        onSubscriptionActive(SCHOLARSHIPS_SUBSCRIPTION, { failure: UnauthorizedPage }),
      )(ScholarshipsFrame)}
    >
      <IndexRedirect to="merit-scholarships" />
      <Route
        name="meritScholarships"
        path="merit-scholarships"
        component={ScholarshipsPage}
      />
    </Route>
    {process.env.GRAPHIQL ? <Route path="graphiql" getComponent={loadGraphIQL} /> : ''}
    <Redirect from="*" to="/" />
  </Route>
);
